import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { toneIconBgClasses } from '@shared/utils/statusTone';
import { Card, Badge, Button } from '@shared/components/ui';
import { Activity, ArrowLeft, Server, Database, HardDrive } from 'lucide-react';

interface SystemHealthDetails {
  agents: {
    total: number;
    online: number;
    offline: number;
    details: Array<{
      id: string;
      name: string;
      status: string;
      version: string;
      os_version: string;
      last_heartbeat?: string;
      ip_address?: string;
    }>;
  };
  backups: {
    total_configs: number;
    active_schedules: number | null;
    last_24h: {
      total: number;
      successful: number;
      failed: number;
    };
    last_backup?: {
      name: string;
      status: string;
      timestamp: string;
    };
  };
  storage: {
    used_bytes: number;
    limit_bytes: number;
    used_gb: number;
    limit_gb: number;
    usage_percent: number | null;
    backup_count: number;
  };
  system_status: {
    overall: string;
    message: string;
    agents_online: number;
    agents_total: number;
  };
}

export default function HealthDetails() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('agents');
  const [healthData, setHealthData] = useState<SystemHealthDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      void fetchHealthDetails();
    }
  }, []);

  const fetchHealthDetails = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) throw new Error('missing-auth');

      const [summaryRes, agentsRes] = await Promise.all([
        fetch('/api/v1/dashboard/summary', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/agents', { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (!summaryRes.ok) throw new Error('summary-unavailable');

      const data = await summaryRes.json();
      const agentsResponse = agentsRes.ok ? await agentsRes.json() : [];
      const agentDetails = Array.isArray(agentsResponse)
        ? agentsResponse
        : Array.isArray(agentsResponse?.agents)
          ? agentsResponse.agents
          : [];
      setError(null);

      // Calculate backup stats
      const recentActivity = Array.isArray(data.recent_activity) ? data.recent_activity : [];
      const last24hBackups = recentActivity.filter(
        (activity: { type?: string; timestamp?: string }) =>
          activity.type === 'backup' &&
          activity.timestamp &&
          new Date(activity.timestamp) > new Date(Date.now() - 86400000)
      );
      const schedules = data.advanced_metrics?.schedules;
      const rawStorage = data.storage_usage || {};
      const usedGB = Math.max(0, Number(rawStorage.used_gb) || 0);
      const limitGB = Math.max(0, Number(rawStorage.limit_gb) || 0);
      const usagePercent =
        limitGB > 0
          ? Math.max(0, Math.min(100, Number(rawStorage.usage_percent) || (usedGB / limitGB) * 100))
          : null;

      setHealthData({
        agents: {
          total: data.system_health?.agents_total || 0,
          online: data.system_health?.agents_online || 0,
          offline:
            (data.system_health?.agents_total || 0) - (data.system_health?.agents_online || 0),
          details: agentDetails,
        },
        backups: {
          total_configs: Number(data.backup_configs_count) || 0,
          active_schedules: Array.isArray(schedules)
            ? schedules.filter((schedule: { is_enabled?: boolean }) => schedule.is_enabled).length
            : null,
          last_24h: {
            total: last24hBackups.length,
            successful: last24hBackups.filter((b: any) =>
              ['completed', 'success'].includes(b.status)
            ).length,
            failed: last24hBackups.filter((b: any) => b.status === 'failed').length,
          },
          last_backup: data.backup_status?.last_backup_time
            ? {
                name: data.backup_status.last_backup_name || 'Unknown',
                status: data.backup_status.last_backup_status || 'unknown',
                timestamp: data.backup_status.last_backup_time,
              }
            : undefined,
        },
        storage: {
          used_bytes: Math.max(0, Number(rawStorage.used_bytes) || 0),
          limit_bytes: Math.max(0, Number(rawStorage.limit_bytes) || 0),
          used_gb: usedGB,
          limit_gb: limitGB,
          usage_percent: usagePercent,
          backup_count: Math.max(0, Number(rawStorage.backup_count) || 0),
        },
        system_status: {
          overall: data.system_health?.status || 'unknown',
          message: data.system_health?.message || 'No status available',
          agents_online: data.system_health?.agents_online || 0,
          agents_total: data.system_health?.agents_total || 0,
        },
      });
    } catch {
      setError(t('healthDetails.error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="py-20 flex justify-center">
          <div
            role="status"
            aria-label={t('healthDetails.loading')}
            className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"
          ></div>
        </div>
      </>
    );
  }

  if (error || !healthData) {
    return (
      <>
        <Card className="space-y-4 py-12 text-center text-state-danger-light dark:text-state-danger-dark">
          <p role="alert" className="font-semibold">
            {error || t('healthDetails.error')}
          </p>
          <Button type="button" variant="secondary" onClick={() => void fetchHealthDetails()}>
            {t('healthDetails.retry')}
          </Button>
        </Card>
      </>
    );
  }

  const overallTone = getStatusTone(healthData.system_status.overall);
  const overallBorderClass =
    overallTone === 'ok'
      ? 'border-l-state-ok-light dark:border-l-state-ok-dark'
      : overallTone === 'warn'
        ? 'border-l-state-warn-light dark:border-l-state-warn-dark'
        : 'border-l-state-danger-light dark:border-l-state-danger-dark';

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              aria-label={t('healthDetails.back')}
              className="p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
            >
              <ArrowLeft size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:text-3xl">
                {t('healthDetails.title')}
              </h1>
              <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark sm:text-base">
                {t('healthDetails.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Overall Status */}
        <Card className={`p-6 border-l-4 ${overallBorderClass}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${toneIconBgClasses[overallTone]}`}>
                <Activity size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {t('healthDetails.statusTitle')}
                </h2>
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t(`healthDetails.messages.${healthData.system_status.message}`, {
                    defaultValue: healthData.system_status.message,
                  })}
                </p>
              </div>
            </div>
            <Badge
              variant={toneToBadgeVariant[overallTone]}
              className="text-sm px-3 py-1 uppercase"
            >
              {t(`healthDetails.status.${healthData.system_status.overall.toLowerCase()}`, {
                defaultValue: healthData.system_status.overall,
              })}
            </Badge>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Agents */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                <Server className="text-brand-600 dark:text-brand-400" />{' '}
                {t('healthDetails.agents')}
              </h3>
              <span className="text-sm font-medium text-text-muted-light dark:text-text-muted-dark">
                {t('healthDetails.onlineOfTotal', {
                  online: healthData.agents.online,
                  total: healthData.agents.total,
                })}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <StatBox label={t('healthDetails.total')} value={healthData.agents.total} />
              <StatBox
                label={t('healthDetails.online')}
                value={healthData.agents.online}
                color="text-state-ok-light dark:text-state-ok-dark"
                bg="bg-state-ok-bg-light dark:bg-state-ok-bg-dark"
              />
              <StatBox
                label={t('healthDetails.offline')}
                value={healthData.agents.offline}
                color="text-state-warn-light dark:text-state-warn-dark"
                bg="bg-state-warn-bg-light dark:bg-state-warn-bg-dark"
              />
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar pr-2">
              {healthData.agents.details.length > 0 ? (
                healthData.agents.details.map((agent) => (
                  <div
                    key={agent.id}
                    className="p-3 border border-surface-border-light dark:border-surface-border-dark rounded-xl flex justify-between items-center"
                  >
                    <div>
                      <p className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark">
                        {agent.name || t('healthDetails.unknownAgent')}
                      </p>
                      <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark font-mono">
                        {t('healthDetails.version', { version: agent.version || '—' })}
                      </p>
                    </div>
                    <Badge
                      variant={toneToBadgeVariant[getStatusTone(agent.status)]}
                      className="text-[10px]"
                    >
                      {t(`healthDetails.status.${agent.status.toLowerCase()}`, {
                        defaultValue: t('healthDetails.statusLabel', { status: agent.status }),
                      })}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="rounded-lg bg-surface-2-light p-3 text-sm text-text-muted-light dark:bg-surface-2-dark dark:text-text-muted-dark">
                  {t('healthDetails.emptyAgents')}
                </p>
              )}
            </div>
          </Card>

          {/* Backups */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                <Database className="text-state-info-light dark:text-state-info-dark" />{' '}
                {t('healthDetails.backups')}
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <StatBox
                label={t('healthDetails.configs')}
                value={healthData.backups.total_configs}
              />
              <StatBox
                label={t('healthDetails.activeSchedules')}
                value={healthData.backups.active_schedules ?? t('healthDetails.notAvailable')}
                color="text-state-info-light dark:text-state-info-dark"
                bg="bg-state-info-bg-light dark:bg-state-info-bg-dark"
              />
            </div>

            <div className="pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
              <p className="text-xs font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-3">
                {t('healthDetails.latestBackupEvents')}
              </p>
              <div className="grid grid-cols-3 gap-2">
                <StatBox
                  label={t('healthDetails.total')}
                  value={healthData.backups.last_24h.total}
                  compact
                />
                <StatBox
                  label={t('healthDetails.success')}
                  value={healthData.backups.last_24h.successful}
                  color="text-state-ok-light dark:text-state-ok-dark"
                  compact
                />
                <StatBox
                  label={t('healthDetails.failed')}
                  value={healthData.backups.last_24h.failed}
                  color="text-state-danger-light dark:text-state-danger-dark"
                  compact
                />
              </div>
            </div>
          </Card>

          {/* Storage */}
          <Card className="p-6">
            <h3 className="mb-6 flex items-center gap-2 text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
              <HardDrive className="text-state-warn-light dark:text-state-warn-dark" />{' '}
              {t('healthDetails.storage')}
            </h3>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm font-bold mb-2">
                  <span className="text-text-muted-light dark:text-text-muted-dark">
                    {t('healthDetails.storageUsage')}
                  </span>
                  <span className="text-right text-text-primary-light dark:text-text-primary-dark">
                    {new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, {
                      maximumFractionDigits: 2,
                    }).format(healthData.storage.used_gb)}{' '}
                    GB /{' '}
                    {healthData.storage.limit_gb > 0
                      ? `${new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 2 }).format(healthData.storage.limit_gb)} GB (${new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 1 }).format(healthData.storage.usage_percent ?? 0)}%)`
                      : t('healthDetails.limitUnknown')}
                  </span>
                </div>
                {healthData.storage.usage_percent !== null && (
                  <div className="w-full bg-surface-border-light dark:bg-surface-border-dark rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        healthData.storage.usage_percent > 90
                          ? 'bg-state-danger-light dark:bg-state-danger-dark'
                          : 'bg-brand-500'
                      }`}
                      style={{ width: `${healthData.storage.usage_percent}%` }}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <StatBox
                  label={t('healthDetails.backupCount')}
                  value={healthData.storage.backup_count}
                />
                <StatBox
                  label={t('healthDetails.averageSize')}
                  value={
                    healthData.storage.backup_count > 0
                      ? `${new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(healthData.storage.used_gb / healthData.storage.backup_count)} GB`
                      : t('healthDetails.notAvailable')
                  }
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

interface StatBoxProps {
  label: string;
  value: number | string;
  color?: string;
  bg?: string;
  compact?: boolean;
}

function StatBox({
  label,
  value,
  color = 'text-slate-900 dark:text-white',
  bg = 'bg-surface-2-light dark:bg-surface-2-dark',
  compact,
}: StatBoxProps) {
  return (
    <div className={`text-center rounded-xl ${bg} ${compact ? 'p-2' : 'p-4'}`}>
      <div
        className={`${typeof value === 'number' ? `font-black ${compact ? 'text-lg' : 'text-2xl'}` : 'text-sm font-semibold leading-tight sm:text-base'} ${color}`}
      >
        {value}
      </div>
      <div className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-wider">
        {label}
      </div>
    </div>
  );
}
